import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, DollarSign, CheckCircle2, Clock, Target } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('mosaic_session_token');

const CAT_LABEL = {
  sses:          'Student Stability Kits',
  print:         'Print Materials',
  digital:       'Digital / Toolkits',
  teacher_tools: 'Teacher Tools',
  bundle:        'Bundles',
  other:         'Other',
};

const fmtUSD = (n) => '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function WinRateStrip() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const token = getToken();
    axios.get(`${API}/api/orders/analytics/win-rate`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);

  if (err) return null; // silently hide if unauth or error
  if (!data) return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-500" data-testid="winrate-loading">Loading analytics…</div>
  );

  const cats = Object.entries(data.by_category || {}).filter(([_, v]) => v.quotes > 0);

  return (
    <div className="rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 via-white to-amber-50 p-4 mb-5" data-testid="winrate-strip">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Target size={18} className="text-indigo-700" />
          <h3 className="font-bold text-gray-900">Quote → Order Conversion</h3>
          <span className="text-xs text-gray-500">live pipeline analytics</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span>Walk-in POs (no prior quote): <b className="text-gray-800">{data.walk_in_orders}</b></span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
        <Stat icon={CheckCircle2} color="emerald" label="Win Rate" value={`${data.win_rate_pct.toFixed(1)}%`} sub={`${data.quotes_won} of ${data.quotes_total} quotes`} testid="wr-count" />
        <Stat icon={DollarSign} color="indigo" label="$ Win Rate" value={`${data.win_rate_dollar_pct.toFixed(1)}%`} sub={`${fmtUSD(data.won_usd)} / ${fmtUSD(data.pipeline_usd)}`} testid="wr-dollar" />
        <Stat icon={TrendingUp} color="blue" label="Open Pipeline" value={fmtUSD(data.open_pipeline_usd)} sub={`${data.quotes_open} open quotes`} testid="wr-pipeline" />
        <Stat icon={CheckCircle2} color="emerald" label="Won Revenue" value={fmtUSD(data.won_usd)} sub={`${data.quotes_won} converted`} testid="wr-revenue" />
        <Stat icon={Clock} color="amber" label="Avg. Close Time" value={data.avg_days_to_convert != null ? `${data.avg_days_to_convert} days` : '—'} sub="quote → order" testid="wr-speed" />
      </div>

      {cats.length > 0 && (
        <div className="mt-4 pt-3 border-t border-indigo-100">
          <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">By Product Family</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {cats.map(([cat, v]) => (
              <div key={cat} className="rounded-lg border border-gray-200 bg-white px-3 py-2" data-testid={`wr-cat-${cat}`}>
                <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold">{CAT_LABEL[cat] || cat}</div>
                <div className="flex items-baseline justify-between mt-1">
                  <span className="text-lg font-bold text-gray-900">{v.win_rate_pct.toFixed(0)}%</span>
                  <span className="text-[11px] text-gray-500">{v.won}/{v.quotes}</span>
                </div>
                <div className="text-[11px] text-gray-500">{fmtUSD(v.won_usd)} / {fmtUSD(v.pipeline_usd)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const COLORS = {
  indigo:  { bg: 'bg-indigo-100',  text: 'text-indigo-700' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  blue:    { bg: 'bg-blue-100',    text: 'text-blue-700' },
  amber:   { bg: 'bg-amber-100',   text: 'text-amber-700' },
};

function Stat({ icon: Icon, color, label, value, sub, testid }) {
  const c = COLORS[color] || COLORS.indigo;
  return (
    <div className="flex items-start gap-2 bg-white rounded-lg border border-gray-200 p-3" data-testid={testid}>
      <div className={`w-8 h-8 rounded-md ${c.bg} flex items-center justify-center shrink-0`}>
        <Icon size={16} className={c.text} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">{label}</div>
        <div className="font-bold text-gray-900 text-lg leading-tight">{value}</div>
        <div className="text-[11px] text-gray-500 truncate">{sub}</div>
      </div>
    </div>
  );
}
